// @ts-strict-ignore
import { useTranslation } from "react-i18next";

import { Spacer } from "@aviary";
import { useFlippers } from "@shared/context/FlippersContext";
import { useSearchParams } from "@shared/react-router-dom/react-router-dom";
import type { PatientAttributes } from "@unauthenticated/shared/data/types/PatientAttributes.d";
import { InvitedByBanner } from "@unauthenticated/signupSignin/components/InvitedByBanner/InvitedByBanner";
import { PoweredByFullscript } from "@unauthenticated/signupSignin/components/PoweredByFullscript/PoweredByFullscript";
import { SignUpHeader } from "@unauthenticated/signupSignin/components/SignUpHeader/SignUpHeader";
import { SignUpSubtitle } from "@unauthenticated/signupSignin/components/SignUpSubtitle/SignUpSubtitle";
import { SignUpTitle } from "@unauthenticated/signupSignin/components/SignUpTitle/SignUpTitle";
import { StoreLandingLayout } from "@unauthenticated/signupSignin/components/StoreLandingLayout/StoreLandingLayout";
import { StoreLogo } from "@unauthenticated/signupSignin/components/StoreLogo/StoreLogo";
import { StoreOffersPill } from "@unauthenticated/signupSignin/components/StoreOffersPill/StoreOffersPill";
import { l } from "@unauthenticated/signupSignin/locales/i18n";
import { EmailCheckForm } from "@unauthenticated/signupSignin/scenes/PatientAuthStartPage/EmailCheckForm/EmailCheckForm";

import * as styles from "./PatientAuthStartPage.styles";

interface Props {
  patientAttributes: PatientAttributes;
  additionalAttributes?: any;
  ssoErrors?: string;
  storeId?: string;
  isPreview?: boolean;
  isStoreOwner: boolean;
  activePatientPromotion?: boolean;
  isStorefronts?: boolean;
}

const PatientAuthStartPage = ({
  patientAttributes,
  additionalAttributes,
  ssoErrors,
  storeId,
  isPreview = false,
  isStoreOwner,
  activePatientPromotion,
  isStorefronts,
}: Props) => {
  const [
    isVanguardUhCustomExperienceEnabled,
    isVanguardAscensionCustomExperienceEnabled,
    isQualityStorySignupEnabled,
  ] = useFlippers(
    "vanguard_uh_custom_experience",
    "vanguard_ascension_custom_experience",
    "platypus_quality_story_signup"
  );
  const { t } = useTranslation();
  const { storeLogo, organizationLogo, storeName, pracAvatar, isStoreIntake, iconLogo, storeSlug } =
    patientAttributes;
  const [searchParameters] = useSearchParams();
  const isIntake = searchParameters.get("intake") === "true";
  const showStoreOffersPill = !isStoreIntake || (isStoreIntake && !isIntake);
  const signUpTitle = isStorefronts ? t(l.common.enterYourEmail) : storeName;

  return (
    <StoreLandingLayout
      storeName={storeName}
      storeId={storeId}
      storeSlug={storeSlug}
      pracAvatar={pracAvatar}
      isPreview={isPreview}
      isStoreOwner={isStoreOwner}
      isActivePromotion={activePatientPromotion}
      isStorefronts={isStorefronts}
      organizationLogo={organizationLogo}
      iconLogo={iconLogo}
      showDeliveryCountry
    >
      {storefrontConfig => (
        <>
          <SignUpHeader isStorefronts={isStorefronts}>
            {!isStorefronts && <StoreLogo storeLogo={storeLogo} storeName={storeName} />}
            <SignUpTitle title={signUpTitle} />
            {!isStorefronts && !!isQualityStorySignupEnabled && (
              <SignUpSubtitle subtitle={t(l.patientRxAuth.QualityStorySubtitle)} />
            )}
            {!isStorefronts && !!showStoreOffersPill && !isVanguardUhCustomExperienceEnabled && (
              <StoreOffersPill patientAttributes={patientAttributes} />
            )}
          </SignUpHeader>
          <InvitedByBanner storeName={storeName} />
          {!isStorefronts && !!isQualityStorySignupEnabled ? (
            <div css={styles.headerSpacer} />
          ) : (
            <Spacer
              height={
                isStorefronts ||
                isVanguardUhCustomExperienceEnabled ||
                isVanguardAscensionCustomExperienceEnabled
                  ? "one"
                  : "double"
              }
            />
          )}
          <EmailCheckForm
            patientAttributes={patientAttributes}
            additionalAttributes={additionalAttributes}
            ssoErrors={ssoErrors}
            storeId={storeId}
            isPreview={isPreview}
            isStorefronts={isStorefronts}
            customAuthDisclaimer={storefrontConfig?.customAuthDisclaimer}
          />
          {!isStorefronts && !!isQualityStorySignupEnabled && <PoweredByFullscript />}
        </>
      )}
    </StoreLandingLayout>
  );
};

export { PatientAuthStartPage };
